export type TimeSlotLike = { time: string };

export type TimeGridConfig = {
  baseMinutes: number;
  intervalMinutes: number;
  rowPx: number;
};

function hhmmToMinutes(hhmm: string): number {
  const t = (hhmm || '').slice(0, 5);
  const [h, m] = t.split(':').map(Number);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
}

export function deriveTimeGridConfig(timeSlots: TimeSlotLike[], rowPx: number, fallbackBase = '08:00', fallbackIntervalMinutes = 15): TimeGridConfig {
  const t0 = (timeSlots?.[0]?.time || fallbackBase).slice(0, 5);
  const t1 = (timeSlots?.[1]?.time || '').slice(0, 5);
  const a = hhmmToMinutes(t0);
  const b = t1 ? hhmmToMinutes(t1) : a + fallbackIntervalMinutes;
  const diff = b - a;
  return {
    baseMinutes: a,
    intervalMinutes: diff > 0 ? diff : fallbackIntervalMinutes,
    rowPx,
  };
}

export function timeToTopPx(time: string, cfg: TimeGridConfig): number {
  const mins = hhmmToMinutes(time);
  const minutesFromBase = mins - cfg.baseMinutes;
  return (minutesFromBase / cfg.intervalMinutes) * cfg.rowPx;
}

export function durationToHeightPx(startTime: string, endTime: string, cfg: TimeGridConfig): number {
  const s = hhmmToMinutes(startTime);
  const e = hhmmToMinutes(endTime);
  const durationMinutes = e - s;
  return (durationMinutes / cfg.intervalMinutes) * cfg.rowPx;
}

export type OverlapLayout = {
  /** 0-based Spaltenindex */
  column: number;
  /** Anzahl Spalten in der Overlap-Gruppe (>= 1) */
  columns: number;
};

export type OverlapEventLike = {
  id: string;
  startTime: string; // HH:MM
  endTime: string;   // HH:MM
};

/**
 * Weist überlappenden Events innerhalb einer "Collision Group" Spalten zu,
 * sodass sie nebeneinander gerendert werden können.
 *
 * - Events, die sich direkt/indirekt überlappen (Kette), teilen sich die gleiche `columns`-Breite.
 * - Events, die sich nur berühren (end == start), gelten als NICHT überlappend.
 */
export function layoutOverlappingEvents<T extends OverlapEventLike>(events: T[]): Array<T & OverlapLayout> {
  if (!events || events.length === 0) return [];
  if (events.length === 1) return [{ ...events[0], column: 0, columns: 1 }];

  const normalized = events.map((ev) => ({
    ev,
    id: String(ev.id),
    start: hhmmToMinutes(ev.startTime),
    end: hhmmToMinutes(ev.endTime),
  })).map((x) => ({
    ...x,
    // defensiv: end darf nicht < start sein (sonst active pruning kaputt)
    end: Number.isFinite(x.end) ? Math.max(x.end, x.start) : x.start,
    start: Number.isFinite(x.start) ? x.start : 0,
  }));

  normalized.sort((a, b) => {
    if (a.start !== b.start) return a.start - b.start;
    // längere zuerst (stabilere Spalten bei gleichem Start)
    const da = a.end - a.start;
    const db = b.end - b.start;
    if (da !== db) return db - da;
    return a.id.localeCompare(b.id);
  });

  type ActiveItem = { id: string; end: number; column: number };
  let active: ActiveItem[] = [];
  let freeColumns: number[] = [];

  let currentGroup = -1;
  const groupMax: number[] = [];
  const groupById = new Map<string, number>();
  const colById = new Map<string, number>();

  const beginGroup = () => {
    currentGroup += 1;
    groupMax[currentGroup] = 0;
    freeColumns = [];
    active = [];
  };

  for (const item of normalized) {
    const start = item.start;

    // abgelaufene Events aus active entfernen (end <= start => keine Überlappung)
    if (active.length > 0) {
      const stillActive: ActiveItem[] = [];
      for (const a of active) {
        if (a.end <= start) {
          freeColumns.push(a.column);
        } else {
          stillActive.push(a);
        }
      }
      active = stillActive;
      if (freeColumns.length > 1) freeColumns.sort((x, y) => x - y);
    }

    if (active.length === 0) {
      beginGroup();
    }

    const col = freeColumns.length > 0 ? (freeColumns.shift() as number) : active.length;
    colById.set(item.id, col);
    groupById.set(item.id, currentGroup);
    active.push({ id: item.id, end: item.end, column: col });
    groupMax[currentGroup] = Math.max(groupMax[currentGroup] || 0, active.length);
  }

  return normalized.map(({ ev, id }) => {
    const group = groupById.get(id) ?? 0;
    const columns = Math.max(1, groupMax[group] ?? 1);
    const column = Math.max(0, colById.get(id) ?? 0);
    return { ...(ev as any), column, columns };
  });
}

export function overlapLayoutToColumnStyle(layout: OverlapLayout, opts?: { gutterPx?: number; gapPx?: number }): { left: string; width: string } {
  const gutterPx = opts?.gutterPx ?? 4; // entspricht inset-x-1 (0.25rem) ungefähr
  const gapPx = opts?.gapPx ?? 4;
  const columns = Math.max(1, layout.columns || 1);
  const column = Math.max(0, Math.min(columns - 1, layout.column || 0));

  if (columns === 1) {
    return {
      left: `${gutterPx}px`,
      width: `calc(100% - ${2 * gutterPx}px)`,
    };
  }

  // Symmetrische "Innenränder" (gutter) und Spaltengap zwischen parallelen Events.
  return {
    left: `calc(${gutterPx}px + ${gapPx / 2}px + ((100% - ${2 * gutterPx}px) * ${column}) / ${columns})`,
    width: `calc(((100% - ${2 * gutterPx}px) / ${columns}) - ${gapPx}px)`,
  };
}



/**
 * Kurse und freistehende Probestunden eines Tages **gemeinsam** anordnen.
 *
 * Vorher rechnete jede Sorte für sich: die Kurse in der Tagesspalte, die Probestunden
 * beim Rendern darüber. Beide bekamen Spalte 0 von 1 und lagen deshalb aufeinander,
 * sobald zur selben Zeit ein Kurs und eine Probestunde standen. Der Schlüssel trennt
 * die Sorten (`kurs:7`, `probe:12`), damit gleiche IDs sich nicht überschreiben.
 */
export function layoutDayEntries(
  entries: Array<{ key: string; startTime: string; endTime: string }>,
): Map<string, OverlapLayout> {
  const laid = layoutOverlappingEvents(
    entries.map((entry) => ({ id: entry.key, startTime: entry.startTime, endTime: entry.endTime })),
  );
  return new Map(laid.map((item) => [String(item.id), { column: item.column, columns: item.columns }]));
}

/** Schlüssel der beiden Sorten — an einer Stelle, damit sie nicht auseinanderlaufen. */
export const dayEntryKey = {
  kurs: (id: string | number) => `kurs:${id}`,
  probestunde: (id: string | number) => `probe:${id}`,
};
